import React, { useState, useEffect } from 'react';
import './PayrollManager.css';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router-dom';
import { useRef } from 'react';
import OuraNav from "../components/OuraNav";
import fetchBase from "../utils/fetchBase";
import LoadingOverlay from "../components/LoadingOverlay";

const PayrollManager = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('tab') || 'uploaded';
    });
    const hotTableRef = useRef(null);
    const [hasSyncedOnce, setHasSyncedOnce] = useState(false);
    const [sheetIndex, setSheetIndex] = useState(0);



    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === 'live') {
            setActiveTab('live');
        }
    }, [location.search]);

    const [uploadedPayrollName, setUploadedPayrollName] = useState(
        () => localStorage.getItem('uploadedPayrollName') || ''
    );
    const [uploadedPayrollDate, setUploadedPayrollDate] = useState(
        () => localStorage.getItem('uploadedPayrollDate') || ''
    );
    const [livePayrollData, setLivePayrollData] = useState([]);
    const [statePayrollData, setStatePayrollData] = useState([]);
    const [recentUploads, setRecentUploads] = useState([]);
    const [archivedPayrolls, setArchivedPayrolls] = useState([]);
    // Pay Period State
    const [activePayPeriod, setActivePayPeriod] = useState(null);
    const [showStartModal, setShowStartModal] = useState(false);
    const [payPeriodMonth, setPayPeriodMonth] = useState(() => {
            const now = new Date();
            return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        });
        const [payPeriodHalf, setPayPeriodHalf] = useState("first");

        const getPayPeriodDates = (monthStr, half) => {
        const [year, month] = monthStr.split("-").map(Number);
        if (half === "first") {
            return {
                start: `${year}-${String(month).padStart(2, '0')}-01`,
                end: `${year}-${String(month).padStart(2, '0')}-15`
            };
        }
        const lastDay = new Date(year, month, 0).getDate();
        return {
            start: `${year}-${String(month).padStart(2, '0')}-16`,
            end: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
        };
    };

    const { start: payPeriodStartDate, end: payPeriodEndDate } = getPayPeriodDates(payPeriodMonth, payPeriodHalf);
    const [overlay, setOverlay] = useState(null);
    const [billingPreview, setBillingPreview] = useState(null);
    const [checkedTimeLogIds, setCheckedTimeLogIds] = useState([]);
    const [transmitResults, setTransmitResults] = useState(null);
    const [transmitting, setTransmitting] = useState(false);
    const [lastClosedPeriod, setLastClosedPeriod] = useState(null);
    const [adminTimesheets, setAdminTimesheets] = useState([]);
    const [selectedTimesheetClient, setSelectedTimesheetClient] = useState(null);
    const [timesheetSubTab, setTimesheetSubTab] = useState("live");

    useEffect(() => {
        if (activeTab !== 'timesheets') return;
        const authHeader = "Bearer " + localStorage.getItem("jwt_token");
        fetch(`${fetchBase}/time-log/admin/timesheets`, {
            headers: { Authorization: authHeader }
        })
            .then(res => res.json())
            .then(data => setAdminTimesheets(Array.isArray(data) ? data : []))
            .catch(err => console.error("Failed to fetch admin timesheets:", err));
    }, [activeTab]);

    useEffect(() => {
        if (activePayPeriod !== null) return;
        fetch(`${fetchBase}/api/pay-period/last-closed`, {
            headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") }
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data && data.id) setLastClosedPeriod(data); })
            .catch(() => {});
    }, [activePayPeriod]);

    const openBillingPreview = async () => {
        try {
            const res = await fetch(`${fetchBase}/api/billing/preview?payPeriodId=${(activePayPeriod || lastClosedPeriod).id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") }
            });
            const data = await res.json();
            if (res.ok && Array.isArray(data)) {
                setBillingPreview(data);
                setCheckedTimeLogIds(data.flatMap(c => c.shifts.map(s => s.timeLogId)));
                setTransmitResults(null);
            } else {
                alert(data.error || "Failed to load billing preview.");
            }
        } catch (err) {
            console.error("Billing preview failed:", err);
        }
    };

    const toggleTimeLog = (id) => {
        setCheckedTimeLogIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const confirmTransmit = async () => {
        setTransmitting(true);
        try {
            const res = await fetch(`${fetchBase}/api/billing/transmit`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("jwt_token") },
                body: JSON.stringify({ payPeriodId: (activePayPeriod || lastClosedPeriod).id, timeLogIds: checkedTimeLogIds })
            });
            const data = await res.json();
            setTransmitResults(data.results || [{ status: "FAILED", error: data.error }]);
        } catch (err) {
            setTransmitResults([{ status: "FAILED", error: "Network error" }]);
        } finally {
            setTransmitting(false);
        }
    };

    useEffect(() => {
        const fetchRecentUploads = async () => {
            try {
                const response = await fetch(`${fetchBase}/api/payroll/recent`, {
                    headers: {
                        'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setRecentUploads(data);
                } else {
                    console.error("❌ Failed to fetch recent uploads");
                }
            } catch (err) {
                console.error("❌ Error fetching recent uploads:", err);
            }
        };

        (async () => {
            await fetchRecentUploads();
        })();
    }, []);

    useEffect(() => {
        if (activeTab === 'history') {
            const fetchArchivedPayrolls = async () => {
                try {
                    const response = await fetch(`${fetchBase}/api/payroll/archive`, {
                        headers: {
                            'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setArchivedPayrolls(data);
                    } else {
                        console.error("❌ Failed to fetch archived payrolls");
                    }
                } catch (err) {
                    console.error("❌ Error fetching archived payrolls:", err);
                }
            };

            fetchArchivedPayrolls();
        }
    }, [activeTab]);

    // ⏪ Fetch latest payroll info if not in localStorage
    useEffect(() => {
        const fetchLatestInfo = async () => {
            const storedName = localStorage.getItem('uploadedPayrollName');
            const storedDate = localStorage.getItem('uploadedPayrollDate');

            // Only fetch if missing
            if (!storedName || !storedDate) {
                try {
                    const response = await fetch(`${fetchBase}/api/payroll/latest-info`, {
                        headers: {
                            'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                        }
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.filename && data.date) {
                            setUploadedPayrollName(data.filename);
                            setUploadedPayrollDate(data.date);
                            localStorage.setItem('uploadedPayrollName', data.filename);
                            localStorage.setItem('uploadedPayrollDate', data.date);
                        }
                    } else {
                        console.error("❌ Failed to fetch latest payroll info");
                    }
                } catch (err) {
                    console.error("❌ Error fetching latest payroll info:", err);
                }
            }
        };

        fetchLatestInfo();
    }, []);

    // Load live payroll sheet AND sync current pay period state from backend on page load
    useEffect(() => {
        loadLivePayrollSheet();

        const fetchActivePayPeriod = async () => {
            try {
                const response = await fetch(`${fetchBase}/api/pay-period/active`, {
                    headers: {
                        'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                    }
                });

                if (response.ok) {
                    const text = await response.text();

                    if (text) {
                        setActivePayPeriod(JSON.parse(text));
                    } else {
                        setActivePayPeriod(null);
                    }
                }
            } catch (err) {
                console.error("❌ Failed to fetch active pay period:", err);
            }
        };

        fetchActivePayPeriod();
    }, []);

    useEffect(() => {
        if (activeTab === 'live') {
            loadLivePayrollSheet().catch((err) => {
                console.error('❌ Error loading sheet when switching to Live tab:', err);
            });
        }
    }, [activeTab]);




    const syncLivePayrollSheet = async () => {
        try {
            await fetch(`${fetchBase}/api/payroll/live-sync`, {
                headers: {
                    'Authorization': "Bearer " + localStorage.getItem("jwt_token"),
                },
            });
        } catch (error) {
            console.error("❌ Failed to sync live payroll sheet:", error);
        }
    };

    const loadLivePayrollSheet = async () => {
            try {
                console.log("📥 Loading live payroll sheet from backend...");
                await new Promise(resolve => setTimeout(resolve, 1000));

                const response = await fetch(`${fetchBase}/api/payroll/live-synced`, {
                headers: {
                    'Authorization': "Bearer " + localStorage.getItem("jwt_token"),
                },
            });

            // ... rest of your code

            const blob = await response.blob();
            console.log("📦 Blob size:", blob.size);

            if (blob.size === 0) {
                console.warn("⚠️ Received an empty file from backend.");
                return;
            }

            const arrayBuffer = await blob.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const vaSheet = workbook.Sheets[workbook.SheetNames[0]];
            const stateSheet = workbook.Sheets[workbook.SheetNames[1]];

            const vaData = XLSX.utils.sheet_to_json(vaSheet, { header: 1 });
            const stateData = stateSheet
                ? XLSX.utils.sheet_to_json(stateSheet, { header: 1 })
                : [];

            console.log("📊 VA Sheet Data Preview:", vaData);
            console.log("📊 STATE Sheet Data Preview:", stateData);

            setLivePayrollData(vaData);
            setStatePayrollData(stateData);
            setHasSyncedOnce(true);
            console.log("✅ Live payroll sheet loaded successfully");

            //localStorage.setItem('livePayrollSheet', JSON.stringify(sheetData));
            // ✅ Save fallback values to localStorage if missing
           // if (!localStorage.getItem('uploadedPayrollName')) {
            //    const generatedName = "Live_Payroll_" + new Date().toLocaleDateString();
            //    localStorage.setItem('uploadedPayrollName', generatedName);
            //    setUploadedPayrollName(generatedName);
        //    }
        //    if (!localStorage.getItem('uploadedPayrollDate')) {
        //        const generatedDate = new Date().toISOString().split('T')[0];
        //        localStorage.setItem('uploadedPayrollDate', generatedDate);
        //        setUploadedPayrollDate(generatedDate);
        //    }

        } catch (error) {
            console.error("❌ Error loading live payroll uploaded:", error);
        }
    };



    const handleManualSave = async () => {
        try {
            const vaData = hotTableRef.current?.hotInstance?.getData() || livePayrollData;

            console.log("💾 MANUAL SAVE VA DATA:", vaData);
            console.log("💾 MANUAL SAVE STATE DATA:", statePayrollData);

            const response = await fetch(`${fetchBase}/api/payroll/save`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("jwt_token"),
                },
                body: JSON.stringify({
                    vaData,
                    stateData: statePayrollData
                }),
            });

            const text = await response.text();
            console.log("💾 SAVE RESPONSE:", text);

            if (response.ok) {
                    setOverlay({ message: "✅ Changes saved successfully!", status: "success" });
                    setTimeout(() => setOverlay(null), 2500);
                } else {
                    setOverlay({ message: "❌ Failed to save changes.", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                }
            } catch (error) {
                console.error("❌ Save error:", error);
                setOverlay({ message: "❌ An error occurred while saving.", status: "error" });
                setTimeout(() => setOverlay(null), 2500);
            }
    };

    const handleSheetChange = async () => {

        console.log(" #### AUTOSAVE TRIGGERED #### ");

            if (hotTableRef.current) {
                const updatedData = hotTableRef.current.hotInstance.getData();

                console.log("UPDATED SNAPSHOT:", updatedData);

                setLivePayrollData(updatedData);

                console.log("⚠️ STATE (IMMEDIATE AFTER SET):", livePayrollData);
                console.log("⚠️ SNAPSHOT USED FOR SAVE:", updatedData);

                console.log("VA STATE BEFORE SEND:", livePayrollData);
                console.log("VA UPDATED SNAPSHOT:", updatedData);

                try {

                    console.log("🚨 AUTOSAVE FIRE CHECK");
                    console.log("VA LENGTH:", updatedData?.length);
                    console.log("STATE LENGTH:", statePayrollData?.length);

                    const response = await fetch(`${fetchBase}/api/payroll/save`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + localStorage.getItem("jwt_token")
                        },
                        body: JSON.stringify({
                            vaData: updatedData,
                            stateData: statePayrollData
                        })
                    });

                    const text = await response.text();
                    console.log("SAVE RESPONSE:", text);

                    if (!response.ok) {
                        console.error("❌ Failed to save sheet to backend");
                    } else {
                        console.log("✅ Sheet saved to backend");
                    }
                } catch (err) {
                    console.error("❌ Error saving sheet:", err);
                }
            }
        };

    const refreshPayPeriodAndSheet = async () => {
        try {
            // 1. Always fetch DB truth first
            const res = await fetch(`${fetchBase}/api/pay-period/active`, {
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("jwt_token")
                }
            });

            if (res.ok) {
                const text = await res.text();
                setActivePayPeriod(text ? JSON.parse(text) : null);
            }

            // 2. THEN safely reload Excel file AFTER state is confirmed
            await loadLivePayrollSheet();

        } catch (err) {
            console.error("❌ Failed to refresh pay period + sheet:", err);
        }
};

    const handleStartPayPeriod = async () => {
        try {
            console.log("🟢 Starting pay period:", payPeriodStartDate, "→", payPeriodEndDate);

            const response = await fetch(
                `${fetchBase}/api/pay-period/start?startDate=${payPeriodStartDate}&endDate=${payPeriodEndDate}`,
                {
                    method: "POST",
                    headers: {
                        "Authorization": "Bearer " + localStorage.getItem("jwt_token")
                    }
                }
            );

            if (response.ok) {
                console.log("✅ Pay period started successfully");
                setShowStartModal(false);
                setLivePayrollData([]);
                setStatePayrollData([]);
                setHasSyncedOnce(false);
                await refreshPayPeriodAndSheet();
            } else {
                console.error("❌ Failed to start pay period");
                setOverlay({ message: "❌ Failed to start pay period", status: "error" });
                setTimeout(() => setOverlay(null), 2500);
            }
        } catch (error) {
            console.error("❌ Error starting pay period:", error);
            setOverlay({ message: "❌ Error occurred while starting pay period", status: "error" });
            setTimeout(() => setOverlay(null), 2500);
        }
    };


    const handleClosePayPeriod = async () => {
        try {
            console.log("🔴 Closing pay period...");
            const response = await fetch(`${fetchBase}/api/pay-period/close`, {
                method: "POST",
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("jwt_token")
                }
            });

            if (response.ok) {
                console.log("✅ Pay period closed successfully");
                setOverlay({ message: "✅ Pay period closed successfully", status: "success" });
                setTimeout(() => setOverlay(null), 2500);

                await refreshPayPeriodAndSheet();

                // ✅ Immediately refresh archive list
                const archiveResponse = await fetch(`${fetchBase}/api/payroll/archive`, {
                    headers: {
                        'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                    }
                });

                if (archiveResponse.ok) {
                    const data = await archiveResponse.json();
                    setArchivedPayrolls(data);
                }
            }
            else {
                    setOverlay({ message: "❌ Failed to close pay period", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
                }
                } catch (error) {
                    console.error("❌ Error closing pay period:", error);
                    setOverlay({ message: "❌ Error occurred while closing pay period", status: "error" });
                    setTimeout(() => setOverlay(null), 2500);
            }
    };


    const handleDownload = async () => {
        try {
            // 1. Call the NEW backend endpoint (not XLSX library)
            const response = await fetch(`${fetchBase}/api/payroll/download`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('jwt_token')
                }
            });

            // 2. If server error, stop
            if (!response.ok) {
                throw new Error('Server error: ' + response.status);
            }

            // 3. Get the Excel file from server
            const blob = await response.blob();

            // 4. Create download link and click it
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Live_Payroll_Sheet.xlsx';
            a.click();

            // 5. Clean up
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('❌ Download failed:', error);
            alert('Failed to download. Please try again.');
        }
    };



    return (
        <>
            {overlay && <LoadingOverlay message={overlay.message} status={overlay.status} />}
            <OuraNav
                links={[
                    { label: "Dashboard", href: "/admin-dashboard" },
                    { label: "Activity", href: "/activity" },
                    { label: "Clients", href: "/admin/clients" },
                    { label: "Employees", href: "/employees" },
                    { label: "Calendar", href: "/Scheduler"}
                ]}
            />

            <div className="payroll-wrapper">
                <div className="payroll-header">
                    <h1>Payroll</h1>
                    <input type="date" className="payroll-date" />
                </div>

                <div className="payroll-body">
                    <div className="payroll-sidebar">
                        <button className={activeTab === 'uploaded' ? 'active' : ''} onClick={() => setActiveTab('uploaded')}>
                            Upload
                        </button>
                        <button className={activeTab === 'live' ? 'active' : ''} onClick={() => setActiveTab('live')}>
                            Live Payroll
                        </button>
                            <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
                                History
                            </button>
                            <button className={activeTab === 'timesheets' ? 'active' : ''} onClick={() => setActiveTab('timesheets')}>
                                Timesheets
                            </button>
                        </div>

                            {activeTab === 'timesheets' && (
                                <div style={{ padding: "20px" }}>
                                    <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
                                        <button
                                            onClick={() => { setTimesheetSubTab("live"); setSelectedTimesheetClient(null); }}
                                            style={{
                                                background: timesheetSubTab === "live" ? "#1d1d1f" : "#f5f5f7",
                                                color: timesheetSubTab === "live" ? "#fff" : "#1d1d1f",
                                                border: "none", borderRadius: "8px", padding: "8px 16px",
                                                fontSize: "13px", fontWeight: 600, cursor: "pointer"
                                            }}
                                        >
                                            Live
                                        </button>
                                        <button
                                            onClick={() => { setTimesheetSubTab("created"); setSelectedTimesheetClient(null); }}
                                            style={{
                                                background: timesheetSubTab === "created" ? "#1d1d1f" : "#f5f5f7",
                                                color: timesheetSubTab === "created" ? "#fff" : "#1d1d1f",
                                                border: "none", borderRadius: "8px", padding: "8px 16px",
                                                fontSize: "13px", fontWeight: 600, cursor: "pointer"
                                            }}
                                        >
                                            Created
                                        </button>
                                    </div>
                            {adminTimesheets.length === 0 ? (
                                <p style={{ color: "#6e6e73", fontSize: "14px" }}>No timesheets yet.</p>
                            ) : (
                                adminTimesheets.map(period => (
                                    <div key={period.payPeriodId} style={{ marginBottom: "30px" }}>
                                        <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#1d1d1f", margin: "0 0 14px" }}>
                                            Period {period.startDate} – {period.endDate}
                                        </h3>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                                            {period.clients.map(client => {
                                                const key = `${period.payPeriodId}-${client.clientId}`;
                                                const isExpanded = selectedTimesheetClient === key;
                                                return (
                                                    <div key={key} style={{ width: "100%" }}>
                                                        <div
                                                            onClick={() => setSelectedTimesheetClient(isExpanded ? null : key)}
                                                            style={{
                                                                background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px",
                                                                padding: "16px 20px", cursor: "pointer", width: "220px",
                                                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)"
                                                            }}
                                                        >
                                                            <p style={{ fontWeight: 700, fontSize: "14px", color: "#1d1d1f", margin: 0 }}>{client.clientName}</p>
                                                            <p style={{ fontSize: "12px", color: "#6e6e73", margin: "4px 0 0" }}>
                                                                {client.timesheets.length} employee{client.timesheets.length !== 1 ? "s" : ""}
                                                            </p>
                                                        </div>

                                                                {isExpanded && (
                                                                    <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
                                                                {client.timesheets.map(ts => (
                                                                    <div
                                                                        key={ts.timesheetId}
                                                                        onClick={() => window.open(`/admin/timesheets/${ts.timesheetId}${timesheetSubTab === "created" ? "?mode=created" : ""}`, "_blank")}
                                                                        style={{
                                                                            background: "#fafafa", border: "1px solid #eee", borderRadius: "10px",
                                                                            padding: "10px 14px", cursor: "pointer",
                                                                            display: "flex", justifyContent: "space-between", alignItems: "center"
                                                                        }}
                                                                    >
                                                                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#1d1d1f" }}>{ts.employeeName}</span>
                                                                        <span style={{
                                                                            fontSize: "11px", fontWeight: 600,
                                                                            color: ts.hasEmployeeSignature ? "#34c759" : "#ff9500"
                                                                        }}>
                                                                            {ts.hasEmployeeSignature ? "Signed" : "Pending"}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'uploaded' && (
                        <div className="upload-dash">
                            <div className="upload-box">
                                <div className="upload-dropzone">
                                    Click or drag file to upload
                                </div>
                            </div>

                            <div className="recent-uploads-panel">
                                <div className="recent-uploads-title">Recent Uploads</div>
                                <div className="recent-uploads-grid">
                                    {recentUploads.map((upload, index) => (
                                        <div key={index} className="excel-tile">
                                            <img src="/icons/excel-icon.svg" alt="Excel" />
                                            <p>{upload.filename}</p>
                                            <span className="upload-date">
                                                {new Date(upload.date).toLocaleDateString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                {activeTab === 'history' && (
                    <div className="upload-dash">
                        <div className="recent-uploads-panel">
                            <div className="recent-uploads-title">Archived Payrolls</div>

                            <div className="recent-uploads-grid">
                                {archivedPayrolls.map((file, index) => (
                                    <div
                                        key={index}
                                        className="excel-tile"
                                        onClick={async () => {
                                            try {
                                                const response = await fetch(
                                                    `${fetchBase}/api/payroll/archive/download?filename=${file.filename}`,
                                                    {
                                                        headers: {
                                                            'Authorization': "Bearer " + localStorage.getItem("jwt_token")
                                                        }
                                                    }
                                                );

                                                if (!response.ok) throw new Error("Download failed");

                                                const blob = await response.blob();

                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = file.filename;
                                                a.click();

                                                window.URL.revokeObjectURL(url);

                                            } catch (err) {
                                                console.error("❌ Failed to download archived file:", err);
                                            }
                                        }}
                                    >
                                        <img src="/icons/excel-icon.svg" alt="Excel" />
                                        <p>{file.filename}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                    {activeTab === 'live' && (
                        <div className="live-payroll-panel">
                            {livePayrollData && livePayrollData.length > 0 ? (
                                <>
                                    <div className="live-header">
                                        <input
                                            type="text"
                                            className="live-title-input"
                                            value={uploadedPayrollName}
                                            onChange={(e) => {
                                                setUploadedPayrollName(e.target.value);
                                                localStorage.setItem('uploadedPayrollName', e.target.value);
                                            }}
                                        />
                                        <p className="live-date-label">
                                            {activePayPeriod ? (
                                                <>
                                                    <span>Pay Period Start: {activePayPeriod.startDate}</span>
                                                    {" | "}
                                                    <span>Pay Period End: {activePayPeriod.endDate}</span>
                                                    {" | "}
                                                    <span>Status: {activePayPeriod.status}</span>
                                                </>
                                            ) : (
                                                <span>Generated on: {uploadedPayrollDate}</span>
                                            )}
                                        </p>

                                        <button
                                            onClick={() => {
                                                if (activePayPeriod) {
                                                    handleClosePayPeriod();
                                                } else {
                                                    setShowStartModal(true);
                                                }
                                            }}
                                            style={{
                                                marginTop: "10px",
                                                padding: "8px 16px",
                                                borderRadius: "6px",
                                                backgroundColor: "#1d1d1f",
                                                color: "#fff",
                                                border: "none",
                                                fontWeight: "600",
                                                cursor: "pointer"
                                            }}
                                        >
                                            {activePayPeriod ? "Close Pay Period" : "Start Pay Period"}
                                        </button>
                                    </div>

                                    <div className="excel-preview-section">

                                        <div className="sheet-header-row">

                                            <button
                                                onClick={() => setSheetIndex(sheetIndex === 0 ? 1 : 0)}
                                            >
                                                Toggle Sheet
                                            </button>

                                            <h3 className="sheet-title">
                                                {sheetIndex === 0 ? "VA Payroll" : "State Payroll"}
                                            </h3>

                                        </div>
                                        <div
                                            className="sheet-slider"
                                            style={{
                                                transform: `translateX(-${sheetIndex * 50}%)`
                                            }}
                                        >


                                            {/* VA SHEET */}
                                            <div className="sheet-panel">

                                                {livePayrollData.length > 0 ? (
                                                    <HotTable
                                                        ref={hotTableRef}
                                                        data={livePayrollData}
                                                        colHeaders={true}
                                                        rowHeaders={true}
                                                        width="100%"
                                                        height="500px"
                                                        stretchH="none"
                                                        autoWrapRow={true}
                                                        manualRowResize={true}
                                                        manualColumnResize={true}
                                                        wordWrap={true}
                                                        colWidths={[160, 180, 120, 120, 140, 140, 140, 120, 220]}
                                                        licenseKey="non-commercial-and-evaluation"
                                                        afterChange={(changes, source) => {
                                                            if (source !== 'loadData' && changes) {
                                                                handleSheetChange();
                                                            }
                                                        }}
                                                        afterRenderer={(TD, row, col) => {
                                                            const rowData = livePayrollData[row];

                                                            if (
                                                                rowData &&
                                                                typeof rowData[0] === "string" &&
                                                                rowData[0].toLowerCase().includes("client")
                                                            ) {
                                                                TD.style.fontWeight = "900";          // max bold
                                                                TD.style.fontSize = "16px";           // bigger text
                                                                TD.style.color = "#000";              // force dark text
                                                                TD.style.backgroundColor = "#eaeaea"; // light gray background
                                                                TD.style.borderBottom = "2px solid #000"; // strong separator
                                                            }
                                                        }}

                                                    />
                                                ) : (
                                                    <div className="excel-placeholder-box">
                                                        <p>📄 Loading live payroll sheet...</p>
                                                    </div>
                                                )}

                                            </div>


                                            {/* STATE SHEET (empty for now) */}
                                            <div className="sheet-panel">

                                                {statePayrollData.length > 0 ? (
                                                    <HotTable
                                                        key={JSON.stringify(livePayrollData)}
                                                        data={statePayrollData}
                                                        colHeaders={true}
                                                        rowHeaders={true}
                                                        width="100%"
                                                        height="500px"
                                                        stretchH="last"
                                                        autoWrapRow={true}
                                                        manualRowResize={true}
                                                        manualColumnResize={true}
                                                        wordWrap={true}
                                                        colWidths={[160, 180, 120, 120, 140, 140, 140, 120, 220]}
                                                        licenseKey="non-commercial-and-evaluation"
                                                        ref={hotTableRef}
                                                        afterChange={(changes, source) => {
                                                            if (source !== 'loadData' && changes) {
                                                                handleSheetChange();
                                                            }
                                                        }}
                                                        afterGetCellMeta={(row, col, cellProperties) => {
                                                            const rowData = statePayrollData[row];

                                                            if (
                                                                rowData &&
                                                                typeof rowData[0] === "string" &&
                                                                rowData[0].toLowerCase().includes("client")
                                                            ) {
                                                                cellProperties.className = 'bold-header';
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="excel-placeholder-box">
                                                        <p>📄 No state payroll data.</p>
                                                    </div>
                                                )}

                                            </div>

                                        </div>


                                        <button
                                            className="save-sheet-button"
                                            onClick={handleManualSave}
                                            style={{
                                                marginTop: "1rem",
                                                padding: "10px 20px",
                                                borderRadius: "6px",
                                                backgroundColor: "#1d1d1f",
                                                color: "#fff",
                                                border: "none",
                                                fontWeight: "600",
                                                cursor: "pointer"
                                            }}
                                        >
                                            💾 Save Changes
                                        </button>

                                        <div className="download-button-container">
                                            <button className="download-btn" onClick={handleDownload}>
                                                Download Sheet
                                            </button>
                                            <button
                                                onClick={openBillingPreview}
                                                disabled={!(activePayPeriod?.status === "CLOSED" || (!activePayPeriod && lastClosedPeriod))}
                                                style={{
                                                    background: !(activePayPeriod?.status === "CLOSED" || (!activePayPeriod && lastClosedPeriod)) ? "#a0c4e8" : "#0071e3",
                                                    color: "#fff", border: "none", borderRadius: "8px",
                                                    padding: "10px 18px", fontWeight: 600, fontSize: "13px",
                                                    cursor: !(activePayPeriod?.status === "CLOSED" || (!activePayPeriod && lastClosedPeriod)) ? "default" : "pointer",
                                                    marginLeft: "10px"
                                                }}
                                            >
                                                Bill VA Hours
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="live-placeholder">
                                    <p>No uploaded payroll sheet available yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showStartModal && (
                <div className="modal-overlay">
                    <div className="modal-box">

                        <button
                            className="modal-close"
                            onClick={() => setShowStartModal(false)}
                        >
                            ✕
                        </button>

                        <h3>Start Pay Period</h3>

                        <p>Select the pay period to start:</p>

                            <div style={{ marginTop: "10px" }}>
                                <label>Month:</label>
                                <input
                                    type="month"
                                    value={payPeriodMonth}
                                    onChange={(e) => setPayPeriodMonth(e.target.value)}
                                />
                            </div>

                            <div style={{ marginTop: "10px" }}>
                                <label>Period:</label>
                                <select
                                    value={payPeriodHalf}
                                    onChange={(e) => setPayPeriodHalf(e.target.value)}
                                >
                                    <option value="first">1st – 15th</option>
                                    <option value="second">16th – End of Month</option>
                                </select>
                            </div>

                            <p style={{ marginTop: "10px", fontSize: "13px", color: "#6e6e73" }}>
                                {payPeriodStartDate} to {payPeriodEndDate}
                            </p>

                        <button
                            onClick={handleStartPayPeriod}
                            style={{
                                marginTop: "15px",
                                padding: "8px 16px",
                                backgroundColor: "#1d1d1f",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer"
                            }}
                        >
                            Confirm Start
                        </button>

                    </div>
                            </div>
                        )}
                        {billingPreview && (
                            <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
                                onClick={() => !transmitting && setBillingPreview(null)}>
                                <div style={{ background: "#fff", borderRadius: "14px", padding: "24px", width: "640px", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                    <h3 style={{ fontSize: "17px", fontWeight: 700, margin: "0 0 16px", color: "#1d1d1f" }}>
                                        Bill VA Hours — Review
                                        <span style={{ fontWeight: 400, fontSize: "13px", color: "#6e6e73", marginLeft: "10px" }}>
                                            {(activePayPeriod || lastClosedPeriod) ? `Pay Period: ${(activePayPeriod || lastClosedPeriod).startDate} – ${(activePayPeriod || lastClosedPeriod).endDate}` : ""}
                                        </span>
                                    </h3>

                                    {!transmitResults ? (
                                        <>
                                            {billingPreview.map(client => (
                                                <div key={client.clientId} style={{ marginBottom: "18px" }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                                        <p style={{ fontWeight: 700, fontSize: "14px", margin: 0, color: "#1d1d1f" }}>{client.clientName}</p>
                                                        <p style={{ fontSize: "13px", margin: 0, color: "#6e6e73" }}>{client.totalHours} hrs — ${client.totalCharge}</p>
                                                    </div>
                                                    {!client.ready && (
                                                        <p style={{ fontSize: "12px", color: "#ff3b30", margin: "0 0 6px" }}>
                                                            Missing: {client.missingInfo.join(", ")} — claims for this client will fail until filled in Profile.
                                                        </p>
                                                    )}
                                                    {client.shifts.map(s => (
                                                        <label key={s.timeLogId} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 8px", borderBottom: "1px solid #f0f0f0", fontSize: "12px", color: "#1d1d1f", cursor: "pointer" }}>
                                                            <input type="checkbox" checked={checkedTimeLogIds.includes(s.timeLogId)} onChange={() => toggleTimeLog(s.timeLogId)} />
                                                            <span style={{ width: "80px" }}>{s.date}</span>
                                                            <span style={{ flex: 1 }}>{s.caregiverName}</span>
                                                            <span style={{ width: "130px" }}>
                                                                {new Date(`1970-01-01T${s.clockIn}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – {new Date(`1970-01-01T${s.clockOut}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                                            </span>
                                                            <span style={{ width: "55px", textAlign: "right" }}>{s.hours} hrs</span>
                                                            <span style={{ width: "60px", textAlign: "right" }}>${s.charge}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            ))}
                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                                                <button onClick={() => setBillingPreview(null)} disabled={transmitting}
                                                    style={{ background: "#f5f5f7", color: "#1d1d1f", border: "none", borderRadius: "8px", padding: "9px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                                                    Cancel
                                                </button>
                                                <button onClick={confirmTransmit} disabled={transmitting || checkedTimeLogIds.length === 0}
                                                    style={{ background: transmitting ? "#a0c4e8" : "#0071e3", color: "#fff", border: "none", borderRadius: "8px", padding: "9px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                                                    {transmitting ? "Transmitting..." : `Confirm & Transmit (${checkedTimeLogIds.length})`}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {transmitResults.map((r, i) => (
                                                <div key={i} style={{ padding: "10px 12px", borderRadius: "10px", marginBottom: "10px", background: r.status === "TRANSMITTED" ? "#eafbf0" : "#fdeaea" }}>
                                                    <p style={{ fontWeight: 700, fontSize: "13px", margin: "0 0 2px", color: r.status === "TRANSMITTED" ? "#1d7a3d" : "#c62828" }}>
                                                        {r.clientName || "Claim"} — {r.status}
                                                    </p>
                                                    <p style={{ fontSize: "12px", margin: 0, color: "#6e6e73" }}>
                                                        {r.status === "TRANSMITTED" ? `${r.shiftsBilled} shift(s) billed.` : r.error}
                                                    </p>
                                                </div>
                                            ))}
                                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <button onClick={() => setBillingPreview(null)}
                                                    style={{ background: "#0071e3", color: "#fff", border: "none", borderRadius: "8px", padding: "9px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                                                    Done
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                        );
                    };

export default PayrollManager;